import React from "react";
import { connect } from "react-redux";
import axios from "axios";
import { api_url } from "redux/config.js";
import { withTranslation } from "react-i18next";
import { t } from "i18next";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput";
import SettingsApplications from "@material-ui/icons/SettingsApplications";
import Toast from "views/Toast/Toast";
import EmailSettingPage from "views/EmailSetting/EmailSettingPage";

// Per-account settings hub. Each hotel account has its own config here (Google review place id,
// SMTP, and — later — the Business Profile API connection for importing/replying to reviews).
class SettingsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'google', google_place_id: '', tripadvisor_url: '', facebook_url: '', booking_url: '', loading: false,
      // SMS channel
      sms: { provider: 'twilio', sender_id: '', endpoint: '', api_key: '', api_secret: '', active: true },
      sms_has_key: false, sms_has_secret: false, sms_credits: 0, sms_max: 0,
      sms_test_phone: '', sms_saving: false, sms_testing: false,
      sms_log: [], sms_counts: [],
    };
  }
  componentDidMount() { this.load(); this.loadSms(); this.loadSmsLog(); }

  auth = () => ({ authorization: "Bearer ".concat(this.props.user.token) });

  // ---- SMS channel ----

  loadSmsLog = () => {
    axios({ url: api_url + 'user/sms_log', method: 'get', headers: this.auth() })
      .then(r => {
        if (r.data && r.data.success) this.setState({ sms_log: r.data.data || [], sms_counts: r.data.counts || [] });
      })
      .catch(() => { });
  }

  loadSms = () => {
    axios({ url: api_url + 'user/sms_setting', method: 'get', headers: this.auth() })
      .then(r => {
        if (r.data && r.data.success) {
          const d = r.data.data;
          this.setState({
            sms: d ? {
              provider: d.provider || 'twilio', sender_id: d.sender_id || '', endpoint: d.endpoint || '',
              api_key: '', api_secret: '', active: d.active !== false,
            } : this.state.sms,
            sms_has_key: !!(d && d.has_api_key), sms_has_secret: !!(d && d.has_api_secret),
            sms_credits: r.data.credits || 0, sms_max: r.data.max_per_campaign || 0,
          });
        }
      })
      .catch(() => { });
  }

  setSms = (k, v) => this.setState({ sms: Object.assign({}, this.state.sms, { [k]: v }) });

  saveSms = () => {
    this.setState({ sms_saving: true });
    axios({ url: api_url + 'user/sms_setting', method: 'post', data: this.state.sms, headers: this.auth() })
      .then(r => {
        this.setState({ sms_saving: false });
        if (r.data && r.data.success) { Toast(200, t('Saved')); this.loadSms(); }
        else Toast(400, (r.data && r.data.error) || t('Could not save'));
      })
      .catch(() => { this.setState({ sms_saving: false }); Toast(500); });
  }

  testSms = () => {
    const phone = (this.state.sms_test_phone || '').trim();
    if (!phone) { Toast(400, t('Enter a phone number in international format, e.g. +306912345678')); return; }
    this.setState({ sms_testing: true });
    axios({
      url: api_url + 'user/sms_test', method: 'post',
      data: { phone, text: 'GooQuest test message' }, headers: this.auth(),
    })
      .then(r => {
        this.setState({ sms_testing: false });
        if (r.data && r.data.success) { Toast(200, t('Test message sent')); this.loadSms(); this.loadSmsLog(); }
        else Toast(400, (r.data && r.data.error) || t('Could not send'));
      })
      .catch(() => { this.setState({ sms_testing: false }); Toast(500); });
  }

  load = () => {
    axios({ url: api_url + 'user/account_settings', method: 'get', headers: this.auth() })
      .then(r => {
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.setState({
            google_place_id: d.google_place_id || '', tripadvisor_url: d.tripadvisor_url || '',
            facebook_url: d.facebook_url || '', booking_url: d.booking_url || '',
          });
        }
      })
      .catch(() => { });
  }

  save = () => {
    this.setState({ loading: true });
    const { google_place_id, tripadvisor_url, facebook_url, booking_url } = this.state;
    axios({ url: api_url + 'user/account_settings', method: 'post', data: { google_place_id, tripadvisor_url, facebook_url, booking_url }, headers: this.auth() })
      .then(r => {
        this.setState({ loading: false });
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.setState({
            google_place_id: d.google_place_id || '', tripadvisor_url: d.tripadvisor_url || '',
            facebook_url: d.facebook_url || '', booking_url: d.booking_url || '',
          });
          Toast(200, t('Saved'));
        } else Toast(400, t('Could not save'));
      })
      .catch(() => { this.setState({ loading: false }); Toast(500); });
  }

  tabBtn = (key, label) => {
    const active = this.state.tab === key;
    return (
      <button onClick={() => this.setState({ tab: key })}
        style={{
          appearance: 'none', border: 0, background: 'transparent', fontFamily: 'inherit', fontSize: 14,
          fontWeight: 600, color: active ? '#1b2320' : '#5d6d66', padding: '10px 16px', cursor: 'pointer',
          borderBottom: active ? '2px solid #2f9e44' : '2px solid transparent', marginBottom: -1,
        }}>{label}</button>
    );
  }

  // Delivery log. 'sent' means the operator accepted the message; 'delivered' means it reached the
  // handset, which only becomes known when the provider posts its delivery report back to us.
  renderSmsLog = () => {
    const { sms_log, sms_counts } = this.state;
    if (!sms_log || !sms_log.length) return null;
    const byStatus = {};
    (sms_counts || []).forEach(c => { byStatus[c.status] = { n: Number(c.n) || 0, segs: Number(c.segs) || 0 }; });
    const COLORS = { delivered: '#2f8a45', sent: '#3b6fd4', queued: '#8a988f', failed: '#d1495b' };
    const LABEL = { delivered: t('Delivered'), sent: t('Sent'), queued: t('Queued'), failed: t('Failed') };
    const pill = (s) => (
      <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, color: '#fff', background: COLORS[s] || '#8a988f' }}>
        {LABEL[s] || s}
      </span>
    );
    return (
      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eef2f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t('Delivery log')}</div>
          <button type="button" onClick={this.loadSmsLog} style={{ border: '1px solid #cfd8d3', background: '#fff', color: '#5d6d66', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '5px 12px', borderRadius: 8, cursor: 'pointer' }}>
            ↻ {t('Refresh')}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
          {['delivered', 'sent', 'queued', 'failed'].map(s => byStatus[s] ? (
            <span key={s} style={{ fontSize: 12.5, color: '#5d6d66' }}>
              {pill(s)} <b>{byStatus[s].n}</b> ({byStatus[s].segs} {t('segment(s)')})
            </span>
          ) : null)}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              <tr style={{ color: '#8a988f', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>{t('Phone')}</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>{t('Email')}</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>{t('Status')}</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>{t('segment(s)')}</th>
              </tr>
              {sms_log.slice(0, 15).map(m => (
                <tr key={m.id} style={{ borderTop: '1px solid #eef2f0' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{m.phone}</td>
                  <td style={{ padding: '6px 8px', color: '#8a988f', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email || ''}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {pill(m.status)}
                    {m.error ? <div style={{ color: '#d1495b', fontSize: 11.5, marginTop: 2 }}>{m.error}</div> : null}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{m.segments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // SMS tab. Credentials are write-only: the API never returns them, so the fields stay blank and
  // an empty field on save means "keep what is stored".
  renderSms = () => {
    const { sms, sms_has_key, sms_has_secret, sms_credits, sms_max } = this.state;
    const isTwilio = sms.provider === 'twilio';
    const isInfobip = sms.provider === 'infobip';
    const keyLabel = isTwilio ? t('Account SID') : t('API key');
    const secretLabel = isTwilio ? t('Auth Token') : t('API secret (if required)');
    const stored = (flag) => flag ? <span style={{ color: '#2f8a45', fontSize: 12, fontWeight: 600 }}> ✓ {t('stored')}</span> : null;
    const provBtn = (key, label) => (
      <button onClick={() => this.setSms('provider', key)} style={{
        border: '1px solid ' + (sms.provider === key ? '#2f9e44' : '#cfd8d3'),
        background: sms.provider === key ? '#2f9e44' : '#fff',
        color: sms.provider === key ? '#fff' : '#5d6d66',
        fontFamily: 'inherit', fontSize: 13, fontWeight: 600, padding: '7px 14px',
        borderRadius: 8, cursor: 'pointer', marginRight: 8,
      }}>{label}</button>
    );
    return (
      <div style={{ maxWidth: 680 }}>
        <p style={{ color: '#5d6d66' }}>
          {t('Send survey invitations by SMS as well as email. Messages are charged per segment by your provider.')}
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '10px 0 18px' }}>
          <div style={{ background: '#f2f9f4', border: '1px solid #cfe6d7', borderRadius: 10, padding: '10px 16px' }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8a988f', fontWeight: 700 }}>{t('SMS credits')}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1b2320' }}>{sms_credits}</div>
          </div>
          <div style={{ background: '#f7f9f8', border: '1px solid #e2e8e4', borderRadius: 10, padding: '10px 16px' }}>
            <div style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.06em', color: '#8a988f', fontWeight: 700 }}>{t('Max per campaign')}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1b2320' }}>{sms_max}</div>
          </div>
        </div>

        <label style={{ fontSize: 12, color: '#8a988f', fontWeight: 600 }}>{t('Provider')}</label>
        <div style={{ margin: '6px 0 10px' }}>
          {provBtn('twilio', 'Twilio')}
          {provBtn('infobip', 'Infobip')}
          {provBtn('generic', t('Other (HTTP)'))}
        </div>

        {/* The hotel opens its own account with the provider and is billed directly by them, so
            point them at the signup page and at where the credentials live. */}
        <div style={{ background: '#f7f9f8', border: '1px solid #e2e8e4', borderRadius: 10, padding: '10px 14px', margin: '0 0 14px', fontSize: 12.5, color: '#5d6d66', lineHeight: 1.6 }}>
          {t('You use your own provider account and are billed directly by them. Create an account and copy the credentials here:')}
          <div style={{ marginTop: 6 }}>
            <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fd4', fontWeight: 600, marginRight: 16 }}>
              {t('Create a Twilio account')} ↗
            </a>
            <a href="https://www.infobip.com/signup" target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fd4', fontWeight: 600 }}>
              {t('Create an Infobip account')} ↗
            </a>
          </div>
          <div style={{ marginTop: 6, color: '#8a988f' }}>
            {isTwilio
              ? t('Twilio: Account SID and Auth Token are on the Console dashboard. The sender must be a number you bought, or a Messaging Service SID.')
              : (isInfobip
                ? t('Infobip: the API key and your personal Base URL are on the API keys page of the dashboard.')
                : t('Enter the endpoint and key of your operator.'))}
          </div>
        </div>

        <CustomInput labelText={keyLabel} id="sms_key" formControlProps={{ fullWidth: true }}
          inputProps={{ value: sms.api_key, placeholder: sms_has_key ? '••••••••' : '', onChange: e => this.setSms('api_key', e.target.value) }} />
        {stored(sms_has_key)}

        {(isTwilio || !isInfobip) &&
          <div>
            <CustomInput labelText={secretLabel} id="sms_secret" formControlProps={{ fullWidth: true }}
              inputProps={{ type: 'password', value: sms.api_secret, placeholder: sms_has_secret ? '••••••••' : '', onChange: e => this.setSms('api_secret', e.target.value) }} />
            {stored(sms_has_secret)}
          </div>}

        <CustomInput labelText={isTwilio ? t('Sender (number or Messaging Service SID)') : t('Sender ID')} id="sms_sender"
          formControlProps={{ fullWidth: true }}
          inputProps={{ value: sms.sender_id, onChange: e => this.setSms('sender_id', e.target.value) }} />
        {/* An alphanumeric sender is capped at 11 characters by the GSM standard, and whether it may
            be used at all depends on the destination country — worth saying here, because a name
            that is too long or unregistered fails at the operator, long after Save looks fine. */}
        {!isTwilio &&
          <div style={{ fontSize: 12.5, color: '#8a988f', marginTop: -6, marginBottom: 10 }}>
            {t('The name recipients see, 3 to 11 characters, letters and digits (e.g. AquilaHotel). Leave empty to use your provider account\'s default sender. Some countries require the name to be registered with the operator first — your provider will tell you.')}
            {sms.sender_id && (sms.sender_id.length > 11 || sms.sender_id.length < 3) && !/^\+?\d+$/.test(sms.sender_id) &&
              <div style={{ color: '#c0392b', fontWeight: 600, marginTop: 3 }}>
                ⚠ {t('A sender name must be 3 to 11 characters, or most operators will reject it.')}
              </div>}
          </div>}

        {!isTwilio &&
          <CustomInput labelText={isInfobip ? t('Base URL (e.g. https://xxxxx.api.infobip.com)') : t('Endpoint URL')} id="sms_endpoint"
            formControlProps={{ fullWidth: true }}
            inputProps={{ value: sms.endpoint, onChange: e => this.setSms('endpoint', e.target.value) }} />}

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '12px 0', fontSize: 13.5, color: '#5d6d66', fontWeight: 600 }}>
          <input type="checkbox" checked={!!sms.active} onChange={e => this.setSms('active', e.target.checked)} />
          {t('SMS sending enabled')}
        </label>

        <div>
          <Button color="success" disabled={this.state.sms_saving} onClick={this.saveSms}>{t('Save')}</Button>
        </div>

        <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid #eef2f0' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{t('Send a test message')}</div>
          <div style={{ fontSize: 12.5, color: '#8a988f', marginBottom: 8 }}>
            {t('Uses one credit and goes through the same path as a campaign, so it proves the whole setup.')}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px' }}>
              <CustomInput labelText={t('Phone (e.g. +306912345678)')} id="sms_test_phone" formControlProps={{ fullWidth: true }}
                inputProps={{ value: this.state.sms_test_phone, onChange: e => this.setState({ sms_test_phone: e.target.value }) }} />
            </div>
            <Button color="info" disabled={this.state.sms_testing} onClick={this.testSms}>
              {this.state.sms_testing ? t('Sending…') : t('Send test')}
            </Button>
          </div>
        </div>

        {this.renderSmsLog()}

        <div style={{ marginTop: 18, fontSize: 12.5, color: '#8a988f', lineHeight: 1.6 }}>
          ⚠️ {t('A message containing Greek (or any non-Latin) characters fits only 70 characters per segment instead of 160 — the same text can cost twice as much. Keep invitations short and prefer English for international guests.')}
        </div>
      </div>
    );
  }

  render() {
    const { tab, google_place_id, tripadvisor_url, facebook_url, booking_url } = this.state;
    const pid = (google_place_id || '').trim();
    const reviewUrl = pid ? 'https://search.google.com/local/writereview?placeid=' + encodeURIComponent(pid) : '';
    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="success" icon>
              <CardIcon color="success"><SettingsApplications /></CardIcon>
              <h4 style={{ color: '#333' }}>{t('Settings')}</h4>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8e4', marginBottom: 20, flexWrap: 'wrap' }}>
                {this.tabBtn('google', t('Review links'))}
                {this.tabBtn('email', t('Email setting'))}
                {this.tabBtn('sms', t('SMS'))}
                {this.tabBtn('gbp', t('Business Profile API'))}
              </div>

              {tab === 'google' &&
                <div style={{ maxWidth: 640 }}>
                  <p style={{ color: '#5d6d66' }}>
                    {t('Add your review links so guests can leave a review from the survey thank-you screen. Each platform with a link shows as a button.')}
                  </p>
                  <CustomInput labelText={t('Google Place ID')} id="place_id" formControlProps={{ fullWidth: true }}
                    inputProps={{ value: google_place_id, onChange: e => this.setState({ google_place_id: e.target.value }) }} />
                  <CustomInput labelText={t('TripAdvisor URL')} id="tripadvisor_url" formControlProps={{ fullWidth: true }}
                    inputProps={{ value: tripadvisor_url, onChange: e => this.setState({ tripadvisor_url: e.target.value }) }} />
                  <CustomInput labelText={t('Facebook URL')} id="facebook_url" formControlProps={{ fullWidth: true }}
                    inputProps={{ value: facebook_url, onChange: e => this.setState({ facebook_url: e.target.value }) }} />
                  <CustomInput labelText={t('Booking.com URL')} id="booking_url" formControlProps={{ fullWidth: true }}
                    inputProps={{ value: booking_url, onChange: e => this.setState({ booking_url: e.target.value }) }} />
                  <div style={{ marginTop: 10 }}>
                    <Button color="success" disabled={this.state.loading} onClick={this.save}>{t('Save')}</Button>
                    {reviewUrl &&
                      <a href={reviewUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 14, color: '#3b6fd4', fontWeight: 600 }}>
                        {t('Preview review link')} ↗
                      </a>}
                  </div>
                  <div style={{ marginTop: 16, fontSize: 12.5, color: '#8a988f', lineHeight: 1.5 }}>
                    {t('Find your Place ID with the')}{' '}
                    <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fd4' }}>
                      Google Place ID Finder ↗
                    </a>
                    <br />
                    {t('Tip: enable the review button per score range in each survey\'s End Messages.')}
                  </div>
                </div>}

              {tab === 'email' && <EmailSettingPage />}

              {tab === 'sms' && this.renderSms()}

              {tab === 'gbp' &&
                <div style={{ color: '#5d6d66', fontSize: 14, lineHeight: 1.6, maxWidth: 640 }}>
                  <p>{t('Connect your Google Business Profile to import guest reviews into the reports and reply to them from here.')}</p>
                  <p style={{ color: '#8a988f', fontSize: 13 }}>{t('Coming soon — pending Google API access.')}</p>
                </div>}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}
const mapStateToProps = ({ user }) => ({ user });
export default connect(mapStateToProps)(withTranslation()(SettingsPage));
